import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { mapTimeline } from 'utils/transformers/TimelineTransformer';
import { sluggify } from 'utils/localization/utils';
import { getDocumentsV2, getSignedUrl } from 'utils/DocumentsAPIUtils';
import { openSignedUrlInNewTab } from 'utils/documents';
import { PROOF_OF_DELIVERY, PROOF_OF_PICKUP } from 'config/documents';
import { showToast } from 'stores/ToastStore';

const openSignedUrl = (documentKey, t) => async (event) => {
  event.preventDefault();

  try {
    const { data, errors } = await getSignedUrl(documentKey);

    if (errors?.length > 0) {
      throw new Error(errors[0].message);
    }

    openSignedUrlInNewTab(data?.getSignedUrl?.signedUrl);
  } catch (e) {
    console.error('JobStatus: error getting signed url: ', e);

    showToast(
      t('GET_SIGNED_URL_ERROR'),
      t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
        error: e,
      }),
      'error',
    );
  }
};

class JobStatus extends Component {
  constructor(props) {
    super(props);
    this.state = { documents: [] };
  }

  componentDidMount() {
    if (!this.props.hideAttachment) {
      this.fetchDocuments();
    }
  }

  async fetchDocuments() {
    const {
      job: { stops },
      t,
    } = this.props;

    try {
      const stopIds = stops.map((stop) => stop.id);
      const { data, errors } = await getDocumentsV2(stopIds);

      if (errors?.length > 0) {
        throw new Error(errors[0]);
      }

      const documents = data.getDocumentsV2.documents || [];

      this.setState({ documents });
    } catch (e) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', {
          error: e,
        }),
        'error',
      );
    }
  }

  renderAttachment(timelineEvent) {
    const stopId = timelineEvent.stopId;
    const { t } = this.props;

    if (!stopId) {
      return null;
    }

    const document = this.state.documents.find(
      (document) =>
        document.foreignKeys.includes(stopId) &&
        [PROOF_OF_PICKUP.value, PROOF_OF_DELIVERY.value].includes(
          document.type,
        ),
    );

    if (!document) {
      return null;
    }

    return (
      <div>
        <div className="timeline-attachment">
          <a
            href="#"
            title={document.name}
            onClick={openSignedUrl(document.key, t)}
          >
            <i className="fa fa-cloud-download" />
          </a>
        </div>
      </div>
    );
  }

  getTimelineEvents(timelineEvents) {
    const { hideAttachment, hideReason, t } = this.props;
    const events = timelineEvents.map((timelineEvent) => {
      return (
        <li key={timelineEvent.event} className="timeline-item">
          <div>
            <div className="timeline-info">
              <div className="title">
                <span>{t(sluggify(timelineEvent.event))}</span>
                {!hideAttachment && this.renderAttachment(timelineEvent)}
                {timelineEvent.signatoryField && (
                  <div className="timeline-signatory">
                    <label>{`${t('SIGNATORY')}:`}</label>
                    {timelineEvent.signatoryField}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="timeline-marker" />
          <div className="timeline-content">
            <p className="timeline-title">{timelineEvent.formattedTime}</p>
            <p className="timeline-title">{timelineEvent.location}</p>
            {!hideReason && timelineEvent.reason && (
              <div className="p-2 rounded bg-red-50">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-red-700"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-2 text-xs leading-normal text-red-700">
                    <span className="block mb-1 font-bold text-red-800">
                      {t('EXCEPTION')}
                    </span>
                    <span className="text-xs leading-normal text-red-700">
                      {timelineEvent.reason}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </li>
      );
    });

    return events;
  }

  render() {
    const timelineEvents = this.getTimelineEvents(mapTimeline(this.props.job));

    return (
      <div>
        <ul className="job_status timeline">{timelineEvents}</ul>
      </div>
    );
  }
}

JobStatus.propTypes = {
  job: PropTypes.shape({
    timeline: PropTypes.shape({
      accepted: PropTypes.string.isRequired,
      arrived_at_destination: PropTypes.string.isRequired,
      arriving_at_destination: PropTypes.string.isRequired,
      arriving_at_origin: PropTypes.string.isRequired,
      en_route: PropTypes.string.isRequired,
      job_closed: PropTypes.string.isRequired,
      opened: PropTypes.string.isRequired,
      picked_up_shipment: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  hideAttachment: PropTypes.bool,
  hideReason: PropTypes.bool,
};

JobStatus.defaultProps = {
  hideAttachment: true,
  hideReason: true,
};

export default withTranslation()(JobStatus);
